import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EnhancedVoiceSelectorV2 } from "@/components/voice/EnhancedVoiceSelectorV2";
import { cn } from "@/lib/utils";
import { VoiceSearchService } from "@/services/agent/configuration/VoiceSearchService";
import { SetupAnalysisResult } from "@/types/agent/setup";
import { GenieCategories } from "@/types/shared/categories";
import { Voice } from "@/types/voice/voice";
import { getFieldSuggestions } from "../../utils/smartSuggestions";
import { ReviewSectionCard } from "./ReviewSectionCard";

interface ConfirmationConversationSectionProps {
  analysis: SetupAnalysisResult;
  onChange: (field: keyof SetupAnalysisResult, value: any) => void;
  voices: Voice[];
  voicesLoading: boolean;
  onRefetchVoices: () => void;
  favorites: Voice[];
}

const PERSONALITY_TRAITS = [
  { id: "professional", label: "Professional", description: "Formal and business-like" },
  { id: "friendly", label: "Friendly", description: "Warm and approachable" },
  { id: "empathetic", label: "Empathetic", description: "Understanding and supportive" },
  { id: "direct", label: "Direct", description: "Clear and to the point" },
  { id: "patient", label: "Patient", description: "Calm and thorough" },
  { id: "energetic", label: "Energetic", description: "Upbeat and enthusiastic" },
];

// Infer personality traits from voice preference text
const inferPersonalityTraits = (voicePreference: string): string[] => {
  const lowerPref = voicePreference.toLowerCase();
  const traits: string[] = [];

  const traitKeywords: Record<string, string[]> = {
    professional: ["professional", "business", "formal", "polite"],
    friendly: ["friendly", "warm", "approachable", "welcoming", "supportive"],
    empathetic: ["empathetic", "caring", "understanding", "compassionate", "supportive"],
    direct: ["direct", "clear", "concise", "straightforward", "practical"],
    patient: ["patient", "calm", "gentle", "thorough"],
    energetic: ["energetic", "enthusiastic", "upbeat", "lively", "dynamic"],
  };

  for (const [traitId, keywords] of Object.entries(traitKeywords)) {
    if (keywords.some((keyword) => lowerPref.includes(keyword))) {
      traits.push(traitId);
    }
  }

  return traits;
};

export const ConfirmationConversationSection = ({
  analysis,
  onChange,
  voices,
  voicesLoading,
  onRefetchVoices,
  favorites,
}: ConfirmationConversationSectionProps) => {
  const category = GenieCategories.find((cat) => cat.id === analysis.category) || GenieCategories.find((cat) => cat.id === "other")!;
  const [selectedTraits, setSelectedTraits] = useState<string[]>(analysis.personalityTraits || []);
  const [isSearchingVoice, setIsSearchingVoice] = useState(false);
  const voiceSearchService = useMemo(() => new VoiceSearchService(), []);

  // Find current voice - memoized to update when voiceId or voices change
  const currentVoice = useMemo(() => {
    return voices?.find((v) => v.id === analysis.voiceId);
  }, [voices, analysis.voiceId]);

  // Pick the best matching voice from the preselected list only — never search outside it
  useEffect(() => {
    if (!analysis.voiceId && voices && voices.length > 0) {
      setIsSearchingVoice(true);
      try {
        let selectedVoiceId: string | null = null;

        if (analysis.voicePreference) {
          const searchTerms = voiceSearchService.extractVoiceSearchTerms(analysis.voicePreference);
          const match = voiceSearchService.searchVoicesFromList(voices, searchTerms);
          selectedVoiceId = match?.voice_id ?? null;
        }

        // Fall back to the first preselected voice if no match
        onChange("voiceId", selectedVoiceId ?? voices[0].id);
      } finally {
        setIsSearchingVoice(false);
      }
    }
  }, [analysis.voiceId, analysis.voicePreference, voices, voiceSearchService, onChange]);

  // Initialize personality traits from voicePreference if not already set
  useEffect(() => {
    if ((!analysis.personalityTraits || analysis.personalityTraits.length === 0) && analysis.voicePreference) {
      const inferredTraits = inferPersonalityTraits(analysis.voicePreference);
      if (inferredTraits.length > 0) {
        setSelectedTraits(inferredTraits);
        onChange("personalityTraits", inferredTraits);
      }
    }
  }, [analysis.voicePreference, analysis.personalityTraits, onChange]);

  const toggleTrait = (traitId: string) => {
    const newTraits = selectedTraits.includes(traitId) ? selectedTraits.filter((id) => id !== traitId) : [...selectedTraits, traitId];

    setSelectedTraits(newTraits);
    onChange("personalityTraits", newTraits);
  };

  return (
    <ReviewSectionCard
      title="Voice & Conversation Setup"
      subtitle="Choose your genie's voice and conversation style"
      genieIcon="settings"
      categoryColor={category.color}
    >
      <div className="space-y-8">
        {/* Voice Selector */}
        <div>
          <Label className="text-base sm:text-lg font-semibold text-gray-900 block mb-2">
            Select your genie's voice <span className="text-red-500">*</span>
          </Label>
          <p className="text-xs sm:text-sm text-gray-600 mb-2">Pick a voice that matches your brand personality</p>
          {analysis.voicePreference && <p className="text-xs text-blue-600 mb-3 italic">Suggested: {analysis.voicePreference}</p>}
          {isSearchingVoice && (
            <div className="flex items-center gap-2 mb-3 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Finding the perfect voice for you...</span>
            </div>
          )}
          <EnhancedVoiceSelectorV2
            value={analysis.voiceId || ""}
            onChange={(voiceId: string) => onChange("voiceId", voiceId)}
            voices={voices}
            voicesLoading={voicesLoading}
            currentVoice={currentVoice}
            onRefetchVoices={onRefetchVoices}
            defaultTestText={analysis.firstMessage || "Hey, what do you think about my voice? Do you think I could be one of your genies?"}
            genieConfig={analysis}
            favorites={favorites}
            restrictToVoices
          />
        </div>

        {/* First Message */}
        <div>
          <Label htmlFor="firstMessage" className="text-base sm:text-lg font-semibold text-gray-900 block mb-2">
            First message customers will hear <span className="text-red-500">*</span>
          </Label>
          <p className="text-xs sm:text-sm text-gray-600 mb-3">
            Make it welcoming and explain what your genie can do (minimum 10 words, 20-50 recommended)
          </p>
          <div className="relative">
            <Textarea
              id="firstMessage"
              value={analysis.firstMessage}
              onChange={(e) => onChange("firstMessage", e.target.value)}
              className="text-sm p-3 min-h-[120px]"
              rows={4}
              placeholder={getFieldSuggestions("firstMessage", analysis)}
            />
            {!analysis.firstMessage && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute bottom-2 right-2 text-xs"
                onClick={() => onChange("firstMessage", getFieldSuggestions("firstMessage", analysis))}
              >
                Use AI Suggestion
              </Button>
            )}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-500">Required field</span>
            <span className="text-xs text-gray-500">{analysis.firstMessage?.split(/\s+/).filter(Boolean).length || 0} words</span>
          </div>
        </div>

        {/* Personality & Tone */}
        <div>
          <Label className="text-base sm:text-lg font-semibold text-gray-900 block mb-2">Pick your genie's personality</Label>
          <p className="text-xs sm:text-sm text-gray-600 mb-2">Select traits that match your brand</p>
          {analysis.voicePreference && selectedTraits.length > 0 && (
            <p className="text-xs text-green-600 mb-3 italic">We've pre-selected traits based on your voice preference</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {PERSONALITY_TRAITS.map((trait) => (
              <button
                key={trait.id}
                onClick={() => toggleTrait(trait.id)}
                className={cn(
                  "px-3 py-2 rounded-lg border-2 text-left transition-all",
                  selectedTraits.includes(trait.id) ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300",
                )}
              >
                <p className="font-semibold text-sm sm:text-base">{trait.label}</p>
                <p className="text-xs sm:text-sm text-gray-600">{trait.description}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </ReviewSectionCard>
  );
};
